import { createContext, useCallback, useContext, useMemo, useRef, type ReactNode, type JSX } from 'react'
import { AuthProvider, useAuth, type AuthProviderProps } from 'react-oidc-context'
import { WebStorageStateStore, type User } from 'oidc-client-ts'
import { ApiClient } from './api'
import { oidcAuthority, type RuntimeConfig } from './config'

/**
 * Cognito's name for the Entra ID provider, as created by the CDK stack. It is
 * passed straight through to the hosted UI, so it must match the provider name
 * in the user pool exactly.
 */
export const ENTRA_PROVIDER = 'MicrosoftEntra'

const ConfigContext = createContext<RuntimeConfig | null>(null)

export function useConfig(): RuntimeConfig {
  const config = useContext(ConfigContext)
  if (!config) throw new Error('useConfig used outside ConfigContext')
  return config
}

/** Where the hosted UI sends the browser back, for both sign-in and sign-out. */
function redirectUri(): string {
  return `${window.location.origin}/`
}

/**
 * Whether `state` is a path in this app: one leading slash, then not a second.
 *
 * `//host/x` and `/\host/x` both start with a slash and both mean another
 * origin. history.replaceState refuses those (it throws a SecurityError), so
 * they were never a redirect, but they left the user stranded on the callback
 * URL instead of landing on the root.
 */
export function isAppPath(state: unknown): state is string {
  return (
    typeof state === 'string' && state.startsWith('/') && !state.startsWith('//') && !state.startsWith('/\\')
  )
}

/**
 * Deep links must survive the round trip: a bookmarked /jobs/:id has to come
 * back to /jobs/:id, not to the root. oidc-client-ts hands `state` back on the
 * User, so the sign-in button stashes the path there.
 */
function onSigninCallback(user: User | undefined): void {
  const target = isAppPath(user?.state) ? user.state : '/'
  window.history.replaceState({}, document.title, target)
}

export function AuthShell({
  config,
  children,
}: Readonly<{ config: RuntimeConfig; children: ReactNode }>): JSX.Element {
  const oidcProps = useMemo<AuthProviderProps>(
    () => ({
      authority: oidcAuthority(config),
      client_id: config.userPoolClientId,
      redirect_uri: redirectUri(),
      response_type: 'code',
      scope: 'openid email profile',
      // Renewal goes through the refresh token Cognito issues for the code
      // flow. Never enable monitorSession: the pool publishes no
      // check_session_iframe and the poll would 404 on a loop.
      automaticSilentRenew: true,
      /*
       * The signed-in session lives in localStorage, not in the tab.
       *
       * oidc-client-ts defaults to sessionStorage, which is scoped to one tab
       * and destroyed when it closes. That default is why opening the app in a
       * second tab asked for a fresh sign-in, and why closing the tab and coming
       * back looked like being logged out — the refresh token that would have
       * renewed the session was thrown away with the tab that held it.
       *
       * The trade-off, stated rather than assumed: a token in localStorage
       * survives a browser restart and is readable by any script on this origin.
       * This origin serves one application, loads no third-party script, and is
       * reachable only after Entra sign-in — and the alternative is a tool that
       * signs people out every time they close a tab, which is what people
       * actually work around by staying signed in somewhere less careful.
       */
      userStore: new WebStorageStateStore({ store: window.localStorage }),
      /*
       * The PKCE verifier and the `state` value, kept in the same place.
       *
       * These only need to survive the redirect out and back, which is one tab —
       * but a sign-in begun in a tab the user then closes leaves an orphan in
       * sessionStorage that nothing ever cleans up, and putting both stores in
       * one place means there is one answer to "where is the session".
       */
      stateStore: new WebStorageStateStore({ store: window.localStorage }),
      onSigninCallback,
    }),
    [config],
  )

  return (
    <ConfigContext.Provider value={config}>
      <AuthProvider {...oidcProps}>{children}</AuthProvider>
    </ConfigContext.Provider>
  )
}

export interface SignInControls {
  signIn: () => void
  label: string
  /** Sign-in goes straight to Microsoft, so the button carries its logo. */
  microsoft?: boolean
}

export function useSignIn(): SignInControls {
  const auth = useAuth()
  const config = useConfig()

  const signIn = useCallback(() => {
    // extraQueryParams rather than a hand-built hosted-UI URL: signinRedirect is
    // what creates and stores the PKCE verifier and the state value, and a URL
    // assembled by hand comes back to a callback that cannot validate either.
    // identity_provider skips Cognito's own account chooser so the user lands
    // directly on the Microsoft sign-in page.
    void auth.signinRedirect({
      state: `${window.location.pathname}${window.location.search}`,
      ...(config.entraEnabled ? { extraQueryParams: { identity_provider: ENTRA_PROVIDER } } : {}),
    })
  }, [auth, config.entraEnabled])

  return {
    signIn,
    label: config.entraEnabled ? 'Continue with Microsoft' : 'Sign in',
    microsoft: config.entraEnabled,
  }
}

export function useSignOut(): () => Promise<void> {
  const auth = useAuth()
  const config = useConfig()

  return useCallback(async () => {
    // Cognito publishes no end_session_endpoint in its discovery document, so
    // signoutRedirect() throws. The hosted UI /logout route is the only way to
    // clear the pool's session cookie; skipping it means the next sign-in
    // silently reuses the old session and looks like the logout never happened.
    await auth.removeUser()
    const params = new URLSearchParams({
      client_id: config.userPoolClientId,
      logout_uri: redirectUri(),
    })
    window.location.assign(`${config.cognitoDomain}/logout?${params.toString()}`)
  }, [auth, config.cognitoDomain, config.userPoolClientId])
}

/**
 * The API client, carrying the **ID** token. The access token is not usable
 * here: it carries neither `aud` nor `email`, and the JWT authorizer checks both.
 */
export function useApi(): ApiClient {
  const config = useConfig()
  const auth = useAuth()

  // Read through a ref so a silent renew does not change the client's identity.
  // Keying the memo on the token instead would give every react-query queryFn a
  // new dependency every hour and re-run every query on the screen.
  const tokenRef = useRef<string | undefined>(undefined)
  tokenRef.current = auth.user?.id_token

  return useMemo(
    () => new ApiClient({ baseUrl: config.apiUrl, getToken: () => tokenRef.current }),
    [config.apiUrl],
  )
}
